import { useState, useCallback, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';

interface SearchResult {
  pageNumber: number;
  text: string;
  index: number;
  context: string;
}

interface UsePDFSearchProps {
  numPages: number;
  onPageChange?: (pageNumber: number) => void;
}

export const usePDFSearch = ({ numPages, onPageChange }: UsePDFSearchProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const { toast } = useToast();

  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      setCurrentResultIndex(-1);
      return;
    }

    setIsSearching(true);
    const results: SearchResult[] = [];

    try {
      // Search across all PDF pages
      for (let page = 1; page <= numPages; page++) {
        const textLayer = document.querySelector(`[data-page-number="${page}"] .react-pdf__Page__textContent`);
        if (textLayer) {
          const pageText = textLayer.textContent || '';
          const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
          let match;
          let index = 0;

          while ((match = regex.exec(pageText)) !== null) {
            const start = Math.max(0, match.index - 30);
            const end = Math.min(pageText.length, match.index + match[0].length + 30);
            const context = pageText.substring(start, end);

            results.push({
              pageNumber: page,
              text: match[0],
              index: index++,
              context: context
            });
          }
        }
      }

      setSearchResults(results);
      setCurrentResultIndex(results.length > 0 ? 0 : -1);
      
      if (results.length > 0 && onPageChange) {
        onPageChange(results[0].pageNumber);
      }

      toast({
        title: "Search Complete",
        description: `Found ${results.length} result${results.length !== 1 ? 's' : ''}`,
        duration: 2000
      });
    } catch (error) {
      console.error('Search failed:', error);
      toast({
        title: "Search Failed",
        description: "Could not search PDF content",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  }, [numPages, onPageChange, toast]);

  const navigateToResult = useCallback((index: number) => {
    if (index >= 0 && index < searchResults.length) {
      setCurrentResultIndex(index);
      const result = searchResults[index];
      if (onPageChange) {
        onPageChange(result.pageNumber);
      }
    }
  }, [searchResults, onPageChange]);

  const nextResult = useCallback(() => {
    if (searchResults.length > 0) {
      const nextIndex = (currentResultIndex + 1) % searchResults.length;
      navigateToResult(nextIndex);
    }
  }, [currentResultIndex, searchResults.length, navigateToResult]);

  const previousResult = useCallback(() => {
    if (searchResults.length > 0) {
      const prevIndex = currentResultIndex <= 0 ? searchResults.length - 1 : currentResultIndex - 1;
      navigateToResult(prevIndex);
    }
  }, [currentResultIndex, searchResults.length, navigateToResult]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults([]);
    setCurrentResultIndex(-1);
  }, []);

  const currentResult = useMemo(() => {
    return currentResultIndex >= 0 ? searchResults[currentResultIndex] : null;
  }, [currentResultIndex, searchResults]);

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    currentResult,
    currentResultIndex,
    isSearching,
    performSearch,
    nextResult,
    previousResult,
    clearSearch,
    hasResults: searchResults.length > 0
  };
};